import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/sharp-regular-svg-icons/faArrowLeft';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Drawer } from 'ui-components/components/drawer/drawer';
import { DrawerHeader } from 'ui-components/components/drawer/drawer-header';
import { FlexColumn, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { NotificationService, NOTIFICATION_DURATIONS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { createFbsApplicationRequest } from './create-fbs-application-request';
import styles from './fbs-application-drawer.module.less';
import { messages } from './messages';
import { submitApplication } from '../../fbs.api';
import { ContactDetailsSection } from './sections/contact-details-section/contact-details-section';
import { useContactDetailsStep } from './sections/contact-details-section/use-contact-details-step';
import { FulfillmentCapabilitiesSection } from './sections/fulfillment-capabilities-section/fulfillment-capabilities-section';
import { useFulfillmentCapabilitiesStep } from './sections/fulfillment-capabilities-section/use-fulfillment-capabilities-step';
import { ShipmentVolumeTimelineSection } from './sections/shipment-volume-timeline-section/shipment-volume-timeline-section';
import { useShipmentVolumeTimelineStep } from './sections/shipment-volume-timeline-section/use-shipment-volume-timeline-step';
import { SkuCountSection } from './sections/sku-count-section/sku-count-section';
import { useSkuCountStep } from './sections/sku-count-section/use-sku-count-step';
import { SellerSettings } from '../../../../enums/seller-settings/seller-settings';
import { useOnComponentMount } from '../../../../hooks/use-on-component-mount';
import { getCurrentDateTimeAsUtcIso } from '../../../../services/datetime-service';
import { saveNewSellerSettings } from '../../../../store/seller/seller.actions';
import { Workflows } from '../../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../../tracking/use-workflow-tracking';

const SUBMIT_STEP_ID = 'submit';

/**
 * @param {object} props
 * @param {() => void} props.onClose - Function to close the drawer
 * @returns {JSX.Element} The application drawer component
 */
export const FbsApplicationDrawer = ({ onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const intl = useIntl();
  const dispatch = useDispatch();
  const trackFbsApplicationWorkflow = useTrackWorkflow(Workflows.FBS_APPLICATION_FLOW);

  useOnComponentMount(() => {
    trackFbsApplicationWorkflow(WorkflowSteps.START);
  });

  const {
    fulfillmentCapabilities,
    isComplete: fulfillmentCapabilitiesIsComplete,
    isEditing: fulfillmentCapabilitiesIsEditing,
    handleCapabilityChange,
    onContinue: fulfillmentCapabilitiesOnContinue,
    onEdit: fulfillmentCapabilitiesOnEdit,
  } = useFulfillmentCapabilitiesStep(trackFbsApplicationWorkflow);

  const {
    skuCount,
    isComplete: skuCountIsComplete,
    isEditing: skuCountIsEditing,
    isValid: skuCountIsValid,
    onSkuCountChange,
    onContinue: skuCountOnContinue,
    onEdit: skuCountOnEdit,
  } = useSkuCountStep(trackFbsApplicationWorkflow);

  const {
    shipmentVolume,
    timeline,
    isComplete: shipmentVolumeTimelineIsComplete,
    isEditing: shipmentVolumeTimelineIsEditing,
    isValid: shipmentVolumeTimelineIsValid,
    onShipmentVolumeChange,
    onTimelineChange,
    onContinue: shipmentVolumeTimelineOnContinue,
    onEdit: shipmentVolumeTimelineOnEdit,
  } = useShipmentVolumeTimelineStep(trackFbsApplicationWorkflow);

  const { contactDetails, isValid: contactDetailsIsValid, handleContactDetailChange } = useContactDetailsStep();

  const handleClose = useCallback(() => {
    if (!isSubmitted) {
      trackFbsApplicationWorkflow(WorkflowSteps.CANCEL);
    }

    onClose();
  }, [isSubmitted, trackFbsApplicationWorkflow, onClose]);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);

    const request = createFbsApplicationRequest({
      fulfillmentCapabilities,
      skuCountOptionId: skuCount,
      shipmentVolumeOptionId: shipmentVolume,
      timelineOptionId: timeline,
      contactDetails,
    });

    try {
      const applicationDate = await submitApplication(request);

      dispatch(
        saveNewSellerSettings({
          [SellerSettings.fbsApplicationDate.key]: applicationDate || getCurrentDateTimeAsUtcIso(),
        }),
      );

      setIsSubmitted(true);
      trackFbsApplicationWorkflow(WorkflowSteps.COMPLETE, { stepId: SUBMIT_STEP_ID });
    } catch {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        title: intl.formatMessage(messages.applicationSubmissionFailedTitle),
        content: intl.formatMessage(messages.applicationSubmissionFailedContent),
        duration: NOTIFICATION_DURATIONS.MEDIUM,
      });
      trackFbsApplicationWorkflow(WorkflowSteps.ERROR, { stepId: SUBMIT_STEP_ID });
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, intl, fulfillmentCapabilities, skuCount, shipmentVolume, timeline, contactDetails, trackFbsApplicationWorkflow]);

  const isSubmitDisabled =
    isSubmitting ||
    !fulfillmentCapabilitiesIsComplete ||
    !skuCountIsComplete ||
    !shipmentVolumeTimelineIsComplete ||
    !contactDetailsIsValid;

  return (
    <Drawer onCancel={handleClose} onClose={handleClose}>
      <DrawerHeader
        className={styles['drawer-header']}
        closeButtonAriaLabel={messages.closeDrawerButtonAriaLabel}
        title={<Text textType={TextType.H4} value={messages.drawerHeader} />}
      />
      <div className={styles['drawer-background']}>
        <FlexColumn className={styles['drawer-body']} gap={Gap.Space24}>
          {isSubmitted ? (
            <>
              <Text textType={TextType.H1} value={messages.submissionSuccessTitle} />
              <Text textType={TextType.Paragraph} value={messages.submissionSuccessBody} />
            </>
          ) : (
            <>
              <Text textType={TextType.Paragraph} value={messages.formTitle} />
              <FulfillmentCapabilitiesSection
                fulfillmentCapabilities={fulfillmentCapabilities}
                isComplete={fulfillmentCapabilitiesIsComplete}
                isEditing={fulfillmentCapabilitiesIsEditing}
                onCapabilityChange={handleCapabilityChange}
                onContinue={fulfillmentCapabilitiesOnContinue}
                onEdit={fulfillmentCapabilitiesOnEdit}
              />
              <SkuCountSection
                isComplete={skuCountIsComplete}
                isContinueDisabled={!skuCountIsValid}
                isDisabled={!fulfillmentCapabilitiesIsComplete}
                isEditing={skuCountIsEditing}
                onContinue={skuCountOnContinue}
                onEdit={skuCountOnEdit}
                onSkuCountChange={onSkuCountChange}
                skuCount={skuCount}
              />
              <ShipmentVolumeTimelineSection
                isComplete={shipmentVolumeTimelineIsComplete}
                isContinueDisabled={!shipmentVolumeTimelineIsValid}
                isDisabled={!skuCountIsComplete}
                isEditing={shipmentVolumeTimelineIsEditing}
                onContinue={shipmentVolumeTimelineOnContinue}
                onEdit={shipmentVolumeTimelineOnEdit}
                onShipmentVolumeChange={onShipmentVolumeChange}
                onTimelineChange={onTimelineChange}
                shipmentVolume={shipmentVolume}
                timeline={timeline}
              />
              <ContactDetailsSection
                contactDetails={contactDetails}
                isDisabled={!shipmentVolumeTimelineIsComplete}
                onContactDetailChange={handleContactDetailChange}
              />
            </>
          )}
        </FlexColumn>
      </div>
      <div className={styles['drawer-footer']}>
        <Button className={styles['back-button']} onClick={handleClose} theme={THEME_CONSTANTS.ghost}>
          <FontAwesomeIcon className={styles['back-button-icon']} icon={faArrowLeft} />
          <FormattedMessage {...messages.back} />
        </Button>
        {isSubmitted ? (
          <Button onClick={handleClose} theme={THEME_CONSTANTS.tertiary}>
            <FormattedMessage {...messages.close} />
          </Button>
        ) : (
          <Button disabled={isSubmitDisabled} onClick={handleSubmit} theme={THEME_CONSTANTS.primary}>
            <FormattedMessage {...messages.submit} />
          </Button>
        )}
      </div>
    </Drawer>
  );
};
